import { Search, Eye, Edit, Plus, ArrowRightLeft, Upload, Download, Image as ImageIcon } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { TableSkeleton } from '../components/ui/table-skeleton'

import { DetailModal } from '../components/inventory/modals/DetailModal'
import { AdjustModal } from '../components/inventory/modals/AdjustModal'
import { ImportModal } from '../components/inventory/modals/ImportModal'
import { TransferModal } from '../components/inventory/modals/TransferModal'
import { ReceiveConfirmModal } from '../components/inventory/modals/ReceiveConfirmModal'
import { SendReviewModal } from '../components/inventory/modals/SendReviewModal'
import { AddProductModal } from '../components/inventory/modals/AddProductModal'

import {
  InventoryItem,
  PendingTransfer,
  NewProduct,
} from '../services/inventory.service'

// ---------------------------------------------------------------------------
// Mock data (will eventually come from API)
// ---------------------------------------------------------------------------
const pendingSends: PendingTransfer[] = [
  {
    id: 'TRF-001',
    date: '2026-04-27',
    fromBranch: 'Sucursal Centro',
    toBranch: 'Sucursal Norte',
    products: [
      { name: 'Paracetamol 500mg', quantity: 50 },
      { name: 'Ibuprofeno 400mg', quantity: 30 },
    ],
    status: 'pending',
  },
  {
    id: 'TRF-002',
    date: '2026-04-26',
    fromBranch: 'Sucursal Centro',
    toBranch: 'Sucursal Sur',
    products: [{ name: 'Loratadina 10mg', quantity: 25 }],
    status: 'pending',
  },
]

const pendingReceives: PendingTransfer[] = [
  {
    id: 'TRF-003',
    date: '2026-04-27',
    fromBranch: 'Sucursal Norte',
    toBranch: 'Sucursal Centro',
    products: [
      { name: 'Omeprazol 20mg', quantity: 40 },
      { name: 'Losartán 50mg', quantity: 20 },
    ],
    status: 'pending',
  },
  {
    id: 'TRF-004',
    date: '2026-04-26',
    fromBranch: 'Sucursal Sur',
    toBranch: 'Sucursal Centro',
    products: [{ name: 'Amoxicilina 500mg', quantity: 35 }],
    status: 'pending',
  },
]

const inventoryData: InventoryItem[] = [
  {
    id: 1,
    name: 'Paracetamol 500mg',
    category: 'Analgésicos',
    stock: 250,
    stockCentro: 250,
    stockNorte: 180,
    stockSur: 200,
    minStock: 50,
    price: 45.0,
    cost: 28.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1245',
    expiry: '2026-12-15',
    lastMovement: '2026-04-25',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 2,
    name: 'Ibuprofeno 400mg',
    category: 'Analgésicos',
    stock: 15,
    stockCentro: 15,
    stockNorte: 150,
    stockSur: 160,
    minStock: 30,
    price: 65.0,
    cost: 42.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1892',
    expiry: '2027-03-20',
    lastMovement: '2026-04-26',
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 3,
    name: 'Amoxicilina 500mg',
    category: 'Antibióticos',
    stock: 95,
    stockCentro: 50,
    stockNorte: 95,
    stockSur: 100,
    minStock: 40,
    price: 180.0,
    cost: 120.0,
    branch: 'Sucursal Norte',
    lot: 'LOT-2024-2103',
    expiry: '2026-08-10',
    lastMovement: '2026-04-24',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 4,
    name: 'Loratadina 10mg',
    category: 'Antihistamínicos',
    stock: 140,
    stockCentro: 120,
    stockNorte: 140,
    stockSur: 140,
    minStock: 60,
    price: 120.0,
    cost: 75.0,
    branch: 'Sucursal Sur',
    lot: 'LOT-2024-1567',
    expiry: '2027-01-30',
    lastMovement: '2026-04-27',
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 5,
    name: 'Omeprazol 20mg',
    category: 'Gastroenterología',
    stock: 200,
    stockCentro: 200,
    stockNorte: 160,
    stockSur: 150,
    minStock: 80,
    price: 95.0,
    cost: 58.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1789',
    expiry: '2026-11-25',
    lastMovement: '2026-04-26',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 6,
    name: 'Losartán 50mg',
    category: 'Cardiovascular',
    stock: 8,
    stockCentro: 25,
    stockNorte: 8,
    stockSur: 30,
    minStock: 25,
    price: 150.0,
    cost: 95.0,
    branch: 'Sucursal Norte',
    lot: 'LOT-2024-2245',
    expiry: '2026-10-05',
    lastMovement: '2026-04-23',
    supplier: 'Farmacéutica GNP',
  },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getStockStatus = (stock: number, minStock: number) => {
  if (stock <= minStock * 0.5)
    return { label: 'Crítico', color: 'bg-red-100 text-red-700' }
  if (stock <= minStock)
    return { label: 'Bajo', color: 'bg-yellow-100 text-yellow-700' }
  return { label: 'Normal', color: 'bg-green-100 text-green-700' }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function Inventory() {
  // UI / filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedBranch, setSelectedBranch] = useState('Sucursal Centro')
  const [isLoading] = useState(false)

  // Selected item state (shared between DetailModal & AdjustModal)
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
  const [selectedTransfer, setSelectedTransfer] =
    useState<PendingTransfer | null>(null)

  // Modal visibility state
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReceiveConfirmModal, setShowReceiveConfirmModal] = useState(false)
  const [showSendReviewModal, setShowSendReviewModal] = useState(false)

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const categories = [
    'all',
    ...Array.from(new Set(inventoryData.map((i) => i.category))),
  ]
  const branches = [
    'all',
    ...Array.from(new Set(inventoryData.map((i) => i.branch))),
  ]

  const filteredInventory = inventoryData.filter((item) => {
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory
    const matchesBranch =
      selectedBranch === 'all' || item.branch === selectedBranch
    return matchesSearch && matchesCategory && matchesBranch
  })

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const openDetailModal = (item: InventoryItem) => {
    setSelectedItem(item)
    setShowDetailModal(true)
  }

  const openAdjustModal = (item: InventoryItem) => {
    setSelectedItem(item)
    setShowAdjustModal(true)
  }

  const handleApplyAdjustment = (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
  }) => {
    console.log('Ajuste aplicado:', data)
    setShowAdjustModal(false)
  }

  const handleAddProduct = (product: NewProduct) => {
    console.log('Producto agregado:', product)
    setShowAddProductModal(false)
  }

  const handleTransfer = (data: {
    action: 'request' | 'receive' | 'send'
    products: Array<{ productId: string; quantity: string }>
    fromBranch: string
    toBranch: string
    notes: string
  }) => {
    console.log('Transferencia realizada:', data)
    setShowTransferModal(false)
  }

  const handleSendReview = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowSendReviewModal(true)
  }

  const handleReceiveConfirm = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowReceiveConfirmModal(true)
  }

  const handleConfirmReceive = (
    transfer: PendingTransfer,
    status: Record<
      string,
      { status: 'received' | 'not_received'; quantity: number; reason: string }
    >,
  ) => {
    console.log('Confirmando recepción:', transfer, status)
    setShowReceiveConfirmModal(false)
    setSelectedTransfer(null)
  }

  const handleConfirmSend = (
    transfer: PendingTransfer,
    status: Record<
      string,
      {
        status: 'complete' | 'partial' | 'not_sent'
        quantity: number
        reason: string
      }
    >,
  ) => {
    console.log('Confirmando envío:', transfer, status)
    setShowSendReviewModal(false)
    setSelectedTransfer(null)
  }

  const handleExport = () => {
    console.log('Exportando productos a Excel...')
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Inventario</h1>
          <p className="mt-1 text-sm text-slate-600">
            {filteredInventory.length} productos encontrados
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTransferModal(true)}>
            <ArrowRightLeft className="mr-2 h-4 w-4" />
            Transferencias
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Importar
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
          <Button onClick={() => setShowAddProductModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar Producto
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories
                .filter((c) => c !== 'all')
                .map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger>
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branches
                .filter((b) => b !== 'all')
                .map((branch) => (
                  <SelectItem key={branch} value={branch}>
                    {branch}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Inventory Table */}
      {isLoading ? (
        <TableSkeleton
          headers={['Imagen', 'Producto', 'Categoría', 'Stock', 'Estado', 'Precio', 'Acciones']}
          columns={['avatar-text', 'text-two-lines', 'text', 'text', 'badge', 'text', 'actions']}
          rows={6}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Imagen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredInventory.map((item) => {
                  const status = getStockStatus(item.stock, item.minStock)
                  return (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="h-12 w-12 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded bg-slate-100">
                            <ImageIcon className="h-6 w-6 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">
                          {item.name}
                        </div>
                        <div className="text-sm text-slate-500">
                          Lote: {item.lot}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.category}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-0.5 text-xs">
                          <div
                            className={
                              item.branch === 'Sucursal Centro' ? 'font-bold' : ''
                            }
                          >
                            <span className="text-slate-600">Centro:</span>{' '}
                            {item.stockCentro}
                          </div>
                          {item.branch === 'Sucursal Centro' && (
                            <div className="text-xs text-slate-500">
                              Mín: {item.minStock}
                            </div>
                          )}
                          <div
                            className={
                              item.branch === 'Sucursal Norte' ? 'font-bold' : ''
                            }
                          >
                            <span className="text-slate-600">Norte:</span>{' '}
                            {item.stockNorte}
                          </div>
                          {item.branch === 'Sucursal Norte' && (
                            <div className="text-xs text-slate-500">
                              Mín: {item.minStock}
                            </div>
                          )}
                          <div
                            className={
                              item.branch === 'Sucursal Sur' ? 'font-bold' : ''
                            }
                          >
                            <span className="text-slate-600">Sur:</span>{' '}
                            {item.stockSur}
                          </div>
                          {item.branch === 'Sucursal Sur' && (
                            <div className="text-xs text-slate-500">
                              Mín: {item.minStock}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={status.color}>{status.label}</Badge>
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        ${item.price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openDetailModal(item)}
                            className="rounded p-1 hover:bg-slate-100"
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => openAdjustModal(item)}
                            className="rounded p-1 hover:bg-slate-100"
                          >
                            <Edit className="h-4 w-4 text-slate-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Modals                                                               */}
      {/* ------------------------------------------------------------------ */}

      <DetailModal
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        selectedItem={selectedItem}
      />

      <AdjustModal
        open={showAdjustModal}
        onOpenChange={setShowAdjustModal}
        selectedItem={selectedItem}
        onApplyAdjustment={handleApplyAdjustment}
      />

      <AddProductModal
        open={showAddProductModal}
        onOpenChange={setShowAddProductModal}
        onAddProduct={handleAddProduct}
      />

      <TransferModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        inventory={inventoryData}
        pendingSends={pendingSends}
        pendingReceives={pendingReceives}
        onSendReview={handleSendReview}
        onReceiveConfirm={handleReceiveConfirm}
        onTransfer={handleTransfer}
      />

      <ImportModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
      />

      <ReceiveConfirmModal
        open={showReceiveConfirmModal}
        onOpenChange={setShowReceiveConfirmModal}
        selectedTransfer={selectedTransfer}
        onConfirm={handleConfirmReceive}
      />

      <SendReviewModal
        open={showSendReviewModal}
        onOpenChange={setShowSendReviewModal}
        selectedTransfer={selectedTransfer}
        inventory={inventoryData}
        onConfirm={handleConfirmSend}
      />
    </div>
  )
}
